import React, { FC } from "react";
import { Loader2, ReceiptEuro } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CreateBankTransferDto, GetRechnungDetailsDto } from "../../../../api-client";
import {
  recnungTransactionMethods,
  transactionTypes,
} from "../../../Transactions/Kasse/v2/types/kasse.types";
import { useCreateBankTransfer } from "../../../../stores/Transactions/transaction.mutations";
import { useLocationId, useUserBusinessId } from "../../../../stores/User/User.hooks";

const germanNumberFormatter = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

interface PayRechnungDialogProps {
  open: boolean;
  onClose: () => void;
  rechnung: GetRechnungDetailsDto | null;
  accountId?: string;
  userId?: string;
  onPaymentSuccess: () => void;
}

/**
 * shadcn version of `PayRechnungDialog` (originally MUI). Identical props, data
 * flow and mutation — only the presentation switches to the shadcn Dialog design
 * used across the V2 billing tables. Used only by `NewPatientRechnungTableV2`.
 */
const PayRechnungDialogShad: FC<PayRechnungDialogProps> = ({
  open,
  onClose,
  rechnung,
  accountId,
  userId,
  onPaymentSuccess,
}) => {
  const createBankTransferMut = useCreateBankTransfer();
  const businessId = useUserBusinessId();
  const locationId = useLocationId();

  const handleConfirm = () => {
    if (!rechnung || !accountId || !userId) return;

    // The full due amount is always paid — the dialog is display-only, no editing.
    const payAmount = rechnung.due;

    const dto: CreateBankTransferDto = {
      method: recnungTransactionMethods.BANK_TRANSFER,
      text: "Bezahlung für Rechnung " + rechnung.rechnungNumber,
      type: transactionTypes.EINNAHME,
      amount: payAmount,
      rechnungAllocations: [
        {
          rechnungId: rechnung.id,
          paidAmount: payAmount,
        },
      ],
      accountId: accountId,
      userId: userId,
      patientId: Number(rechnung.patientId) || undefined,
      rxId: rechnung.rxId,
      businessId: businessId,
      locationId: locationId,
    };

    createBankTransferMut.mutate(dto, {
      onSuccess: () => {
        onPaymentSuccess();
        onClose();
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ReceiptEuro className="h-5 w-5 text-[#4189a8]" />
            Rechnung bezahlen
          </DialogTitle>
          <DialogDescription className="text-[15px]">
            Der fällige Betrag wird als Banküberweisung verbucht.
          </DialogDescription>
        </DialogHeader>

        {rechnung && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4 rounded-lg border border-solid border-slate-200 bg-slate-50/60 p-4">
              <div>
                <p className="my-0 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Rechnungnummer
                </p>
                <p className="my-0 mt-0.5 text-[15px] font-medium text-slate-800">
                  {rechnung.rechnungNumber}
                </p>
              </div>
              <div>
                <p className="my-0 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Rezeptnummer
                </p>
                <p className="my-0 mt-0.5 text-[15px] font-medium text-slate-800">
                  {rechnung.rxNumber || "—"}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-solid border-[#4189a8]/30 bg-[#4189a8]/5 px-4 py-3">
              <span className="text-sm font-semibold uppercase tracking-wide text-[#367b9a]">
                Fällig
              </span>
              <span className="text-2xl font-bold text-[#2d6b86]">
                {germanNumberFormatter.format(rechnung.due)} €
              </span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" className="text-base" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            className="bg-gradient-to-b from-[#5aa6c4] to-[#4189a8] text-base text-white hover:from-[#4f9cbb] hover:to-[#3a7f9c]"
            onClick={handleConfirm}
            disabled={createBankTransferMut.isPending}
          >
            {createBankTransferMut.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Bitte warten...
              </>
            ) : (
              "Einreichen"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PayRechnungDialogShad;
